import Autosuggest from 'react-autosuggest';
import Clear from '@material-ui/icons/Clear';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import LinearProgress from '@material-ui/core/LinearProgress';
import MenuItem from '@material-ui/core/MenuItem';
import PropTypes from 'prop-types';
import React from 'react';
import TextField from '@material-ui/core/TextField';
import Typography from 'fuse-ui/components/Typography';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { withStyles } from '@material-ui/core/styles';

const styles = theme => ( {
    container: {
        'float': 'right',
        position: 'relative',
        width: 300,
    },
    error: {
        backgroundColor: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: theme.shadows[3],
        paddingBottom: 0,
        paddingLeft: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
        paddingTop: theme.spacing.unit / 2,
        position: 'absolute',
        width: '100%',
    },
    input: {
        height: 23
    },
    progress: {
        flexGrow: 1,
        left: 0,
        width: '300px',
    },
    suggestion: {
        cusor: 'pointer',
        display: 'block',
    },
    suggestionsContainer: {
        display: 'none'
    },
    suggestionsContainerOpen: {
        backgroundColor: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '2px',
        boxShadow: theme.shadows[3],
        display: 'block',
        left: 0,
        maxHeight: '180px',
        overflowY: 'auto',
        position: 'absolute',
        right: 0,
        zIndex: 1,
    },
    suggestionsList: {
        listStyleType: 'none',
        margin: 0,
        padding: 0,
    },
} );

class DomainSelector extends React.Component{
    constructor( props ){
        super( props );

        this.state = {
            enterPressed: false,
            isLoading: false,
            suggestions: [],
            value: props.selectedDomain,
        };

        this.lastRequestId = null;
        this.minCharacters = 3;
    }

    shouldRenderSuggestions = ( value ) => {
        return value.trim().length >= this.minCharacters;
    }

    getSuggestionValue = ( suggestion ) => {
        return suggestion;
    }

    clearDomain = () => {
        this.onSuggestionsClearRequested();

        this.setState( {
            value: ''
        } );

        this.props.handleDomainClear();
    };

    getClearButton = () => {
        return <InputAdornment position="end">
            <IconButton
                aria-label="Clear domain"
                onClick={this.clearDomain}
                data-automation-id="clear-domain"
            ><Clear /></IconButton>
        </InputAdornment>;
    }

    renderInputComponent = ( inputProps ) => {
        const { classes, inputRef = () => {}, ref, ...other } = inputProps;
        let textFieldInputProps = {
            classes: {
                input: classes.input,
            },
            inputRef: node => {
                ref( node );
                inputRef( node );
            },
        };

        // Only render the clear icon if text is entered
        if( other.value.length ){
            textFieldInputProps.endAdornment = this.getClearButton();
        }

        return (
            <TextField
                fullWidth
                InputProps={textFieldInputProps}
                {...other}
            />
        );
    }

    renderSuggestion = ( suggestion, { query, isHighlighted } ) => {
        const matches = match( suggestion, query );
        const parts = parse( suggestion, matches );

        return (
            <MenuItem selected={isHighlighted} component="div">
                <div>
                    {parts.map( ( part, index ) => {
                        return part.highlight ? (
                            <span key={String( index )} style={{ fontWeight: 500 }}>
                                {part.text}
                            </span>
                        ) : (
                            <strong key={String( index )} style={{ fontWeight: 300 }}>
                                {part.text}
                            </strong>
                        );
                    } )}
                </div>
            </MenuItem>
        );
    }

    renderSuggestionsContainer = ( { containerProps , children, query } ) => {
        const { classes } = this.props;
        const { isLoading } = this.state;
        let result;
        let applyClass;

        if( children ){
            result = children;
            applyClass = classes.suggestionsContainerOpen;
        }
        else{
            if( this.shouldRenderSuggestions( query ) && isLoading ){
                result = <LinearProgress />;
                applyClass = classes.progress;
            }
            else if( this.state.suggestions.length === 0 && query !== this.props.selectedDomain && query.length >= 3 ){
                applyClass = '';
                result = <Typography className={classes.error}> {query} is not a recognized domain </Typography>;
            }
            else if( query.length > 0 && query.length < 3 ){
                applyClass = '';
                result = ( <Typography className={classes.error}>
                Type at least {this.minCharacters} characters
                </Typography> );
            }
        }

        return (
            <div {...containerProps} className={applyClass}>
                { result }
            </div>
        );
    }

    loadSuggestions( value ){
        // Cancel the previous request
        if( this.lastRequestId !== null ){
            clearTimeout( this.lastRequestId );
        }

        this.setState( {
            isLoading: true,
            suggestions: []
        } );

        this.lastRequestId = setTimeout( () => {
            this.props.handleDomainSearch(
                {
                    limit: this.props.resultLimit,
                    matchPos: this.props.matchPos,
                    search: value,
                } )
                .then( normalizedDomains => {
                    return normalizedDomains.allIds.map( id => normalizedDomains.domainsById[id].domainName );
                } )
                .then(
                    ( domains ) => {
                        this.setState( {
                            isLoading: false,
                            suggestions: domains
                        } );

                        if( this.state.enterPressed === true ){
                            if( domains.includes( value ) ){
                                this.onSuggestionSelected( null, { suggestionValue: value } );
                                this.onSuggestionsClearRequested();
                            }
                            else{
                                this.setState( { suggestions: [] } );
                            }
                        }
                    }
                );
        }, 500 );
    }

    onChange = ( event, { newValue } ) => {
        this.setState( {
            value: newValue
        } );
    };

    onKeyDown = ( e ) => {
        this.setState( { enterPressed: false } );

        if( e.key === 'Enter' ){
            this.setState( { enterPressed: true } );
            this.loadSuggestions( this.state.value );
        }
    }

    onSuggestionsFetchRequested = ( { value } ) => {
        this.loadSuggestions( value );
    };

    onSuggestionSelected = ( event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method } ) => {
        this.props.handleDomainSelect( suggestionValue );
    }

    onSuggestionsClearRequested = () => {
        this.setState( {
            enterPressed: false,
            suggestions: [],
        } );
    };

    render(){
        const { classes, automationId } = this.props;
        const { value, suggestions } = this.state;
        const inputProps = {
            classes,
            'data-automation-id': automationId,
            onChange: this.onChange,
            onKeyDown: this.onKeyDown,
            placeholder: 'All domains',
            value,
        };

        return (
            <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                getSuggestionValue={this.getSuggestionValue}
                renderInputComponent={this.renderInputComponent}
                renderSuggestionsContainer={this.renderSuggestionsContainer}
                renderSuggestion={this.renderSuggestion}
                shouldRenderSuggestions={this.shouldRenderSuggestions}
                onSuggestionSelected={this.onSuggestionSelected}
                inputProps={inputProps}
                theme={{
                    container: classes.container,
                    input: classes.input,
                    suggestion: classes.suggestion,
                    suggestionsContainer: classes.suggestionsContainer,
                    suggestionsList: classes.suggestionsList,
                }}
            />
        );
    }
}

DomainSelector.propTypes = {
    automationId: PropTypes.string,
    classes: PropTypes.object,
    handleDomainClear: PropTypes.func.isRequired,
    handleDomainSearch: PropTypes.func.isRequired,
    handleDomainSelect: PropTypes.func.isRequired,
    matchPos: PropTypes.oneOf( ['start', 'middle', 'end'] ),
    resultLimit: PropTypes.number.isRequired,
    selectedDomain: PropTypes.string.isRequired,
};

DomainSelector.defaultProps = {
    matchPos: 'middle',
    resultLimit: 40,
};

export default withStyles( styles )( DomainSelector );
